import { useEffect, useRef, useState } from 'react';
import { answerConcierge } from '@/pages/home/components/conciergeEngine';
import { useFavorites } from '@/hooks/useFavorites';
import GuestGuideAnswer from '@/components/feature/GuestGuideAnswer';
import { PROPERTY_GUEST_GUIDE_OPEN_EVENT } from '@/events/propertyGuestGuide';
import type { PropertyConciergeConfig } from '@/pages/property-detail/types';

type Message = {
  id: string;
  role: 'guest' | 'concierge';
  text: string;
  refs?: string[];
  action?: 'contact';
};

type Props = {
  config: PropertyConciergeConfig;
};

function buildSeedMessages(config: PropertyConciergeConfig): Message[] {
  const seedR = answerConcierge(config.seedQuestion, config.propertyId);
  return [
    {
      id: 'intro',
      role: 'concierge',
      text: config.introText,
    },
    { id: 'seed-g', role: 'guest', text: config.seedQuestion },
    { id: 'seed-c', role: 'concierge', text: seedR.answer, refs: seedR.refs, action: seedR.action },
  ];
}

export default function PropertyConcierge({ config }: Props) {
  const [messages, setMessages] = useState<Message[]>(() => buildSeedMessages(config));
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { favorites } = useFavorites();

  useEffect(() => {
    const openGuide = () => setOpen(true);
    window.addEventListener(PROPERTY_GUEST_GUIDE_OPEN_EVENT, openGuide);

    if (window.location.hash === '#concierge') {
      setOpen(true);
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }

    return () => window.removeEventListener(PROPERTY_GUEST_GUIDE_OPEN_EVENT, openGuide);
  }, []);

  useEffect(() => {
    if (!open) return;

    const originalOverflow = document.body.style.overflow;
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, thinking]);

  const send = (raw: string) => {
    const q = raw.trim();
    if (!q) return;
    setMessages((prev) => [...prev, { id: `g-${Date.now()}`, role: 'guest', text: q }]);
    setInput('');
    setThinking(true);
    setTimeout(() => {
      const result = answerConcierge(q, config.propertyId, favorites);
      setMessages((prev) => [
        ...prev,
        { id: `c-${Date.now()}`, role: 'concierge', text: result.answer, refs: result.refs, action: result.action },
      ]);
      setThinking(false);
    }, 600);
  };

  return (
    <>
      <span id="concierge" className="sr-only" aria-hidden="true" />
      {open && (
        <div
          className="fixed inset-0 z-[90] bg-foreground-950/90 backdrop-blur-sm px-4 py-5 md:px-8 md:py-8 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="The Inspire Insider"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full max-w-[1160px] max-h-[calc(100vh-2.5rem)] overflow-y-auto rounded-2xl bg-foreground-950 text-background-50 shadow-2xl subtle-scroll"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close The Inspire Insider"
              className="absolute right-4 top-4 z-10 w-10 h-10 flex items-center justify-center rounded-lg bg-[#EFE4D6] text-foreground-950 hover:bg-[#E9DCCB] cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg" />
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 p-5 pt-16 md:p-8 lg:p-10">
              <div className="lg:col-span-5">
                <div className="text-[11px] uppercase tracking-[0.24em] text-accent-300 mb-4">
                  <span className="inline-block w-5 h-px bg-accent-500 align-middle mr-3" />
                  The Inspire Insider
                </div>
                <h2 className="font-heading text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em]">
                  Have a question
                  <span className="block italic font-light text-secondary-200"> about the property?</span>
                </h2>
                <p className="mt-6 text-background-100/80 text-base md:text-lg font-light leading-relaxed max-w-md">
                  Ask about {config.propertyName}, compare it with other Inspire properties, or get help
                  deciding whether it fits your trip.
                </p>
                <p className="mt-8 text-xs text-background-100/50 leading-relaxed max-w-md">
                  Booking details are available on this page. For anything personal, the Inspire team can help.
                </p>
              </div>

              <div className="lg:col-span-7">
                <div className="rounded-2xl bg-background-50 text-foreground-900 overflow-hidden flex flex-col h-[min(620px,calc(100vh-9rem))] min-h-[480px] border border-background-200/70">
                  <div className="flex items-center gap-3 px-5 md:px-6 py-4 border-b border-background-200/70 bg-background-100/60">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 flex-shrink-0">
                      <i className="ri-service-line text-sm" />
                    </span>
                    <div>
                      <p className="font-heading text-base leading-tight">The Inspire Insider</p>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-500">{config.propertyName}</p>
                    </div>
                    <span className="ml-auto hidden sm:inline text-[11px] uppercase tracking-[0.2em] text-accent-700">
                      <i className="ri-heart-3-line mr-1" />
                      At your service
                    </span>
                  </div>

                  <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-4 subtle-scroll">
                    {messages.map((m) => (
                      <div
                        key={m.id}
                        className={`flex ${m.role === 'guest' ? 'justify-end' : 'justify-start'} animate-msg-in`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm md:text-[15px] leading-relaxed ${
                            m.role === 'guest'
                              ? 'bg-foreground-950 text-background-50 rounded-br-md'
                              : 'bg-background-100 text-foreground-900 rounded-bl-md'
                          }`}
                        >
                          {m.role === 'concierge' ? (
                            <>
                              <GuestGuideAnswer text={m.text} refs={m.refs} onPropertyNavigate={() => setOpen(false)} />
                            </>
                          ) : (
                            <p>{m.text}</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {thinking && (
                      <div className="flex justify-start animate-msg-in">
                        <div className="bg-background-100 rounded-2xl rounded-bl-md px-4 py-3 text-sm flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-pulse" />
                          <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-pulse [animation-delay:120ms]" />
                          <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-pulse [animation-delay:240ms]" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="px-5 md:px-6 py-3 border-t border-background-200/70 bg-background-100/40">
                    <div className="flex gap-2 overflow-x-auto subtle-scroll pb-1">
                      {config.starters.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => send(s)}
                          className="text-xs whitespace-nowrap px-3 py-2 rounded-lg border border-[#DED0BE] bg-[#EFE4D6] text-foreground-800 hover:border-[#B99F87] hover:text-foreground-950 cursor-pointer transition-colors flex-shrink-0"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      send(input);
                    }}
                    className="px-4 md:px-5 py-4 border-t border-background-200/70 bg-background-50"
                  >
                    <div className="flex items-center gap-3 rounded-xl border border-[#CDBCA8] bg-background-100 px-4 py-2.5 shadow-sm focus-within:border-accent-600 focus-within:bg-background-50 transition-colors">
                      <i className="ri-question-answer-line text-lg text-accent-700" aria-hidden="true" />
                      <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={`Ask about ${config.propertyName}...`}
                        className="min-w-0 flex-1 bg-transparent text-sm md:text-base text-foreground-900 placeholder:text-foreground-500 focus:outline-none py-2"
                      />
                      <button
                        type="submit"
                        disabled={!input.trim()}
                        className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-foreground-950 text-background-50 hover:bg-accent-600 disabled:bg-foreground-300 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer transition-colors"
                        aria-label="Send"
                      >
                        <i className="ri-arrow-up-line" />
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
